import { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import '@livekit/components-styles'
import { LiveKitRoom, VideoConference } from '@livekit/components-react'

const API = process.env.REACT_APP_API_URL

function cabeceras(profesorId) {
  return { headers: { 'x-profesor-id': profesorId } }
}

const ROTULOS_GRABACION = {
  sin_grabar: null,
  grabando: '🔴 Grabando...',
  procesando: '⏳ Guardando la grabación...',
  lista: '✅ Grabación lista',
  error: '⚠ Hubo un problema al grabar'
}

// Textos que ve el estudiante (la barra del profesor se queda en espanol).
const TX = {
  es: { conectando: 'Conectando a la clase...', noComenzado: 'La clase todavía no ha comenzado.',
        seActualiza: 'Esta pantalla se actualiza sola en cuanto el profesor la inicie.',
        errConectar: 'No se pudo conectar a la clase.', volver: '← Volver' },
  en: { conectando: 'Connecting to the class...', noComenzado: "The class hasn't started yet.",
        seActualiza: 'This screen updates automatically as soon as the teacher starts it.',
        errConectar: 'Could not connect to the class.', volver: '← Back' }
}

// Pantalla completa de videollamada para una clase en vivo. Uno de
// usuarioId (estudiante) o profesorId (el profesor asignado a la clase)
// debe venir con valor — el backend decide segun cual llego si quien
// entra es anfitrion (con controles extra) o no.
export default function SalaClaseVivo({ usuarioId, profesorId, claseId, onSalir, idioma = 'es' }) {
  const t = TX[idioma] || TX.es
  const [estado, setEstado] = useState('cargando') // 'cargando' | 'esperando' | 'conectado' | 'error'
  const [datosSala, setDatosSala] = useState(null)  // { token, url, esAnfitrion }
  const [mensajeError, setMensajeError] = useState('')
  const [grabacionEstado, setGrabacionEstado] = useState('sin_grabar')
  const [grabacionUrl, setGrabacionUrl] = useState(null)
  const [accionGrabacion, setAccionGrabacion] = useState(false)
  const pollEsperaRef = useRef(null)
  const pollGrabacionRef = useRef(null)

  const pedirToken = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/videollamada/token`, {
        usuario_id: usuarioId, profesor_id: profesorId, clase_id: claseId
      })
      setDatosSala(r.data)
      setEstado('conectado')
      if (r.data.esAnfitrion) {
        // Al entrar el profesor, la sala queda abierta automaticamente
        // para que los estudiantes inscritos puedan unirse.
        axios.post(`${API}/videollamada/iniciar`, { clase_id: claseId }, cabeceras(profesorId))
          .then(r2 => setGrabacionEstado(r2.data?.datos?.grabacion_estado || 'sin_grabar'))
          .catch(() => {})
      }
      return true
    } catch (err) {
      if (err.response?.status === 409) {
        setEstado('esperando')
      } else {
        setEstado('error')
        setMensajeError(err.response?.data?.error || t.errConectar)
      }
      return false
    }
  }, [usuarioId, profesorId, claseId, t])

  useEffect(() => { pedirToken() }, [pedirToken])

  // Mientras se espera a que el profesor inicie la clase, reintenta cada 10s.
  useEffect(() => {
    if (estado !== 'esperando') return
    pollEsperaRef.current = setInterval(pedirToken, 10000)
    return () => clearInterval(pollEsperaRef.current)
  }, [estado, pedirToken])

  // Mientras se procesa una grabacion recien detenida, consulta el estado
  // cada pocos segundos hasta que quede lista (o falle).
  useEffect(() => {
    if (grabacionEstado !== 'procesando') return
    pollGrabacionRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/videollamada/grabar/estado/${claseId}`)
        const nuevoEstado = r.data?.datos?.grabacion_estado
        if (nuevoEstado && nuevoEstado !== 'procesando') {
          setGrabacionEstado(nuevoEstado)
          setGrabacionUrl(r.data?.datos?.grabacion_url || null)
        }
      } catch { /* se reintenta en el siguiente ciclo */ }
    }, 4000)
    return () => clearInterval(pollGrabacionRef.current)
  }, [grabacionEstado, claseId])

  async function iniciarGrabacion() {
    setAccionGrabacion(true)
    try {
      const r = await axios.post(`${API}/videollamada/grabar/iniciar`, { clase_id: claseId }, cabeceras(profesorId))
      setGrabacionEstado(r.data?.datos?.grabacion_estado || 'grabando')
      setGrabacionUrl(null)
    } catch (err) {
      setGrabacionEstado('error')
    } finally {
      setAccionGrabacion(false)
    }
  }

  async function detenerGrabacion() {
    setAccionGrabacion(true)
    try {
      const r = await axios.post(`${API}/videollamada/grabar/detener`, { clase_id: claseId }, cabeceras(profesorId))
      setGrabacionEstado(r.data?.datos?.grabacion_estado || 'procesando')
    } catch (err) {
      setGrabacionEstado('error')
    } finally {
      setAccionGrabacion(false)
    }
  }

  async function finalizarClase() {
    try {
      await axios.post(`${API}/videollamada/finalizar`, { clase_id: claseId }, cabeceras(profesorId))
    } catch { /* igual salimos aunque falle */ }
    onSalir()
  }

  // ── Pantallas de estado (antes de conectar a la videollamada) ───────
  if (estado === 'cargando') {
    return (
      <PantallaCentrada>
        <div style={{ fontSize: 40, marginBottom: 14 }}>🎥</div>
        <p>{t.conectando}</p>
      </PantallaCentrada>
    )
  }

  if (estado === 'esperando') {
    return (
      <PantallaCentrada>
        <div style={{ fontSize: 40, marginBottom: 14 }}>⏳</div>
        <p style={{ marginBottom: 6 }}>{t.noComenzado}</p>
        <p style={{ fontSize: 13, opacity: .7, marginBottom: 20 }}>{t.seActualiza}</p>
        <BotonSalir onClick={onSalir} t={t} />
      </PantallaCentrada>
    )
  }

  if (estado === 'error') {
    return (
      <PantallaCentrada>
        <div style={{ fontSize: 40, marginBottom: 14 }}>⚠</div>
        <p style={{ marginBottom: 20 }}>{mensajeError}</p>
        <BotonSalir onClick={onSalir} t={t} />
      </PantallaCentrada>
    )
  }

  // ── Conectado: la videollamada en sí ─────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0D1117', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
      {datosSala?.esAnfitrion && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '8px 16px', background: '#0D1F52', color: '#fff', fontSize: 13
        }}>
          <span style={{ fontWeight: 700 }}>🧑‍🏫 Vista del profesor</span>

          {grabacionEstado === 'sin_grabar' || grabacionEstado === 'error' || grabacionEstado === 'lista' ? (
            <button onClick={iniciarGrabacion} disabled={accionGrabacion} style={estiloBotonBarra('#B91C1C')}>
              🔴 Iniciar grabación
            </button>
          ) : grabacionEstado === 'grabando' ? (
            <button onClick={detenerGrabacion} disabled={accionGrabacion} style={estiloBotonBarra('#374151')}>
              ⏹ Detener grabación
            </button>
          ) : null}

          {ROTULOS_GRABACION[grabacionEstado] && (
            <span style={{ opacity: .85 }}>{ROTULOS_GRABACION[grabacionEstado]}</span>
          )}

          {grabacionUrl && (
            <a href={grabacionUrl} target="_blank" rel="noreferrer" style={{ color: '#93C5FD' }}>▶ Ver grabación</a>
          )}

          <button onClick={finalizarClase} style={{ ...estiloBotonBarra('#7F1D1D'), marginLeft: 'auto' }}>
            Finalizar clase para todos
          </button>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0 }}>
        <LiveKitRoom
          serverUrl={datosSala.url}
          token={datosSala.token}
          connect
          audio
          video
          onDisconnected={onSalir}
          style={{ height: '100%' }}
        >
          <VideoConference />
        </LiveKitRoom>
      </div>
    </div>
  )
}

function PantallaCentrada({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0D1117', zIndex: 1000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: '#fff', textAlign: 'center', fontFamily: 'inherit', padding: 20
    }}>
      {children}
    </div>
  )
}

function BotonSalir({ onClick, t }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 20px', background: '#1B3A8C', color: '#fff', border: 'none',
      borderRadius: 10, fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer'
    }}>
      {t.volver}
    </button>
  )
}

function estiloBotonBarra(color) {
  return {
    padding: '6px 14px', background: color, color: '#fff', border: 'none',
    borderRadius: 8, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer'
  }
}
